import { useState, useEffect } from 'react';
import { FormData } from '../../../types/transaction';
import { supabase } from '../../../lib/supabase';
import { Search, X, AlertTriangle } from 'lucide-react';
import { findTransactionsByMandateNumber, describeMandate, ExistingMandate } from '../../../utils/mandateNumber';

interface Copro {
  id: string;
  numero: string;
  residence: string;
}

interface BasicInfoFormProps {
  formData: FormData;
  onChange: (data: FormData) => void;
  readOnly?: boolean;
  /** Transaction en cours de modification : elle ne doit pas se signaler elle-même */
  currentTransactionId?: string;
}

type CoproType = 'lefeuvre' | 'newco' | 'syndic';

export default function BasicInfoForm({ formData, onChange, readOnly = false, currentTransactionId }: BasicInfoFormProps) {
  // Garde-fou : numéro de mandat déjà utilisé par une autre transaction
  const [duplicateMandates, setDuplicateMandates] = useState<ExistingMandate[]>([]);
  // State pour chaque type de copro
  const [coprosLefeuvre, setCoprosLefeuvre] = useState<Copro[]>([]);
  const [coprosNewco, setCoprosNewco] = useState<Copro[]>([]);
  const [coprosSyndic, setCoprosSyndic] = useState<Copro[]>([]);
  
  const [filteredCopros, setFilteredCopros] = useState<Copro[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Vérifier si le numéro de mandat est déjà utilisé (recherche différée à la saisie)
  useEffect(() => {
    if (readOnly) return;

    const mandateNumber = formData.property_number?.trim();
    if (!mandateNumber) {
      setDuplicateMandates([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const found = await findTransactionsByMandateNumber(mandateNumber, currentTransactionId);
        if (!cancelled) setDuplicateMandates(found);
      } catch (err) {
        console.error('Error checking mandate number:', err);
        if (!cancelled) setDuplicateMandates([]);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.property_number, currentTransactionId, readOnly]);

  // Charger les copros selon l'origine sélectionnée
  useEffect(() => {
    if (formData.property_origin === 'lefeuvre') {
      loadCopros('lefeuvre');
    } else if (formData.property_origin === 'newco') {
      loadCopros('newco');
    } else if (formData.property_origin === 'syndic') {
      loadCopros('syndic');
    }
  }, [formData.property_origin]);

  // Filtrer les copros selon la recherche
  useEffect(() => {
    const currentCopros = getCurrentCopros();
    if (searchTerm) {
      const filtered = currentCopros.filter(copro => 
        copro.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
        copro.residence.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCopros(filtered);
    } else {
      setFilteredCopros(currentCopros);
    }
  }, [searchTerm, coprosLefeuvre, coprosNewco, coprosSyndic, formData.property_origin]);

  const getTableName = (type: CoproType): string => {
    switch (type) {
      case 'lefeuvre': return 'copros';
      case 'newco': return 'copros_newco';
      case 'syndic': return 'copros_syndic';
    }
  };

  const loadCopros = async (type: CoproType) => {
    try {
      const tableName = getTableName(type);
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order('numero');

      if (error) throw error;
      
      switch (type) {
        case 'lefeuvre':
          setCoprosLefeuvre(data || []);
          break;
        case 'newco':
          setCoprosNewco(data || []);
          break;
        case 'syndic':
          setCoprosSyndic(data || []);
          break;
      }
      setFilteredCopros(data || []);
    } catch (err) {
      console.error(`Error loading copros (${type}):`, err);
    }
  };

  const getCurrentCopros = (): Copro[] => {
    switch (formData.property_origin) {
      case 'lefeuvre': return coprosLefeuvre;
      case 'newco': return coprosNewco;
      case 'syndic': return coprosSyndic;
      default: return [];
    }
  };

  const getCurrentCoproId = (): string => {
    switch (formData.property_origin) {
      case 'lefeuvre': return formData.copro_id || '';
      case 'newco': return formData.copro_newco_id || '';
      case 'syndic': return formData.copro_syndic_id || '';
      default: return '';
    }
  };

  const getSelectedCoproLabel = () => {
    const coproId = getCurrentCoproId();
    if (!coproId) return '';
    const copros = getCurrentCopros();
    const copro = copros.find(c => c.id === coproId);
    return copro ? `${copro.numero} - ${copro.residence}` : '';
  };

  const handleCoproSelect = (coproId: string) => {
    const updates: Partial<FormData> = {};
    
    switch (formData.property_origin) {
      case 'lefeuvre':
        updates.copro_id = coproId;
        break;
      case 'newco':
        updates.copro_newco_id = coproId;
        break;
      case 'syndic':
        updates.copro_syndic_id = coproId;
        break;
    }
    
    onChange({ ...formData, ...updates });
    setShowDropdown(false);
    setSearchTerm('');
  };

  const handleClearCopro = () => {
    const updates: Partial<FormData> = {};
    
    switch (formData.property_origin) {
      case 'lefeuvre':
        updates.copro_id = '';
        break;
      case 'newco':
        updates.copro_newco_id = '';
        break;
      case 'syndic':
        updates.copro_syndic_id = '';
        break;
    }
    
    onChange({ ...formData, ...updates });
    setSearchTerm('');
  };

  const handleOriginChange = (newOrigin: string) => {
    onChange({ 
      ...formData, 
      property_origin: newOrigin as 'gestion' | 'lefeuvre' | 'newco' | 'syndic' | '',
      copro_id: '',
      copro_newco_id: '',
      copro_syndic_id: '',
      lefeuvre_syndic: false,
      lefeuvre_gestion: false,
      newco_syndic: false,
      newco_gestion: false,
      syndic_syndic: false,
      syndic_gestion: false
    });
    setSearchTerm('');
    setShowDropdown(false);
  };

  const showCoproSelector = ['lefeuvre', 'newco', 'syndic'].includes(formData.property_origin || '');
  const currentCoproId = getCurrentCoproId();

  const getCoproSectionTitle = (): string => {
    switch (formData.property_origin) {
      case 'lefeuvre': return 'Copropriété Lefeuvre';
      case 'newco': return 'Copropriété NewCo';
      case 'syndic': return 'Copropriété Kermarrec Syndic';
      default: return 'Copropriété';
    }
  };

  const getCoproSectionColor = (): string => {
    switch (formData.property_origin) {
      case 'lefeuvre': return 'bg-blue-50';
      case 'newco': return 'bg-green-50';
      case 'syndic': return 'bg-purple-50';
      default: return 'bg-gray-50';
    }
  };

  const hasDuplicateMandate = duplicateMandates.length > 0;

  return (
    <div className="space-y-4">
      {hasDuplicateMandate && (
        <div className="flex gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 min-w-0">
            <p className="font-semibold">
              {duplicateMandates.length > 1
                ? `${duplicateMandates.length} transactions existent déjà avec le numéro de mandat « ${formData.property_number.trim()} »`
                : `Une transaction existe déjà avec le numéro de mandat « ${formData.property_number.trim()} »`}
            </p>
            <ul className="mt-1 space-y-0.5">
              {duplicateMandates.map(t => (
                <li key={t.id} className="text-amber-800">• {describeMandate(t)}</li>
              ))}
            </ul>
            <p className="mt-1.5 text-xs text-amber-700">
              Vérifiez qu'il ne s'agit pas d'un doublon avant d'enregistrer.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numéro de mandat
          </label>
          <input
            type="text"
            value={formData.property_number}
            onChange={(e) => !readOnly && onChange({ ...formData, property_number: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
              hasDuplicateMandate
                ? 'border-amber-400 bg-amber-50/50 focus:ring-amber-400/50'
                : 'border-gray-300 focus:ring-blue-500/50'
            }`}
            required
            readOnly={readOnly}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type de mandat
          </label>
          <select
            value={formData.mandate_type}
            onChange={(e) => !readOnly && onChange({ ...formData, mandate_type: e.target.value as 'exclu' | 'simple' | 'privilège' | 'recherche' | 'délégation' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            required
            disabled={readOnly}
          >
            <option value="simple">Simple</option>
            <option value="exclu">Exclusif</option>
            <option value="privilège">Privilège</option>
            <option value="recherche">Recherche</option>
            <option value="délégation">Délégation</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date du mandat
          </label>
          <input
            type="date"
            value={formData.mandate_date || ''}
            onChange={(e) => !readOnly && onChange({ ...formData, mandate_date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            disabled={readOnly}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type de bien
          </label>
          <select
            value={formData.property_type || ''}
            onChange={(e) => !readOnly && onChange({ 
              ...formData, 
              property_type: (e.target.value as '' | 'Appartement' | 'Maison' | 'Immeuble' | 'Parking' | 'Box' | 'Terrain')
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            disabled={readOnly}
          >
            <option value="">Sélectionner un type</option>
            <option value="Appartement">Appartement</option>
            <option value="Maison">Maison</option>
            <option value="Immeuble">Immeuble</option>
            <option value="Parking">Parking</option>
            <option value="Box">Box</option>
            <option value="Terrain">Terrain</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Vendeur
          </label>
          <input
            type="text"
            value={formData.seller}
            onChange={(e) => !readOnly && onChange({ ...formData, seller: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            required
            readOnly={readOnly}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Acquéreur
          </label>
          <input
            type="text"
            value={formData.buyer}
            onChange={(e) => !readOnly && onChange({ ...formData, buyer: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            required
            readOnly={readOnly}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Origine
          </label>
          <select
            value={formData.property_origin || ''}
            onChange={(e) => !readOnly && handleOriginChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            disabled={readOnly}
          >
            <option value="">Non spécifié</option>
            <option value="gestion">Bien de gestion KI</option>
            <option value="lefeuvre">Bien Lefeuvre</option>
            <option value="newco">Bien NewCo</option>
            <option value="syndic">Bien Kermarrec Syndic</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Origine du lead
          </label>
          <select
            value={formData.lead_origin || ''}
            onChange={(e) => !readOnly && onChange({ 
              ...formData, 
              lead_origin: e.target.value as 'acquereur' | 'prospection_boitage' | 'prospection_porte_a_porte' | 'prospection_pige' | 'prospection_preavis_gestion' | 'prospection_rel_copro' | 'prospection_panneau_picking' | 'prospection_capturama_ademe' | 'prospection_ancien_client_signe' | 'spontane_gestion_loc' | 'spontane_appel_agence' | 'spontane_passage_agence' | 'spontane_reseau_perso' | 'spontane_syndic' | 'spontane_immobilier_entreprise' | 'spontane_temps_fort_campagne_publipostage' | 'spontane_interagence_transac' | 'relance_ancien_client_copro' | 'spontane_mail_boite_transaction' | 'spontane_flyer' | 'relance_ancienne_estim_source_inconnue' | ''
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            disabled={readOnly}
          >
            <option value="">Sélectionner une origine</option>
            <option value="acquereur">Acquéreur</option>
            <option value="prospection_boitage">Prospection - boîtage</option>
            <option value="prospection_porte_a_porte">Prospection - porte à porte</option>
            <option value="prospection_pige">Prospection - pige</option>
            <option value="prospection_preavis_gestion">Prospection - préavis gestion</option>
            <option value="prospection_rel_copro">Prospection - rel copro</option>
            <option value="prospection_panneau_picking">Prospection - panneau (picking)</option>
            <option value="prospection_capturama_ademe">Prospection - capturama - ADEME</option>
            <option value="prospection_ancien_client_signe">Prospection - ancien client signé</option>
            <option value="spontane_gestion_loc">Spontané - gestion loc</option>
            <option value="spontane_appel_agence">Spontané - appel agence</option>
            <option value="spontane_passage_agence">Spontané - passage agence</option>
            <option value="spontane_reseau_perso">Spontané - réseau/perso</option>
            <option value="spontane_syndic">Spontané - syndic</option>
            <option value="spontane_immobilier_entreprise">Spontané - immobilier d'entreprise</option>
            <option value="spontane_temps_fort_campagne_publipostage">Spontané temps fort - campagne publipostage</option>
            <option value="spontane_interagence_transac">Spontané - interagence transac</option>
            <option value="relance_ancien_client_copro">Relance ancien client copro</option>
            <option value="spontane_mail_boite_transaction">Spontané - mail boîte Transaction</option>
            <option value="spontane_flyer">Spontané - flyer</option>
            <option value="relance_ancienne_estim_source_inconnue">Relance ancienne estim (source inconnue)</option>
          </select>
        </div>
      </div>

      {/* Show copro selection for Lefeuvre, NewCo, or Syndic properties */}
      {showCoproSelector && (
        <>
          <div className={`${getCoproSectionColor()} p-4 rounded-lg space-y-4`}>
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {getCoproSectionTitle()} <span className="text-gray-400 text-xs">(optionnel)</span>
              </label>
              
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={showDropdown ? searchTerm : getSelectedCoproLabel()}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Rechercher une copropriété (optionnel)..."
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 bg-white"
                    disabled={readOnly}
                  />
                  {currentCoproId && !readOnly && (
                    <button
                      type="button"
                      onClick={handleClearCopro}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Effacer la sélection"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                
                {showDropdown && !readOnly && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => {
                        setShowDropdown(false);
                        setSearchTerm('');
                      }}
                    />
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div
                        onClick={() => {
                          handleClearCopro();
                          setShowDropdown(false);
                        }}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-200 text-gray-500 italic"
                      >
                        Aucune copropriété
                      </div>
                      {filteredCopros.length > 0 ? (
                        filteredCopros.map((copro) => (
                          <div
                            key={copro.id}
                            onClick={() => handleCoproSelect(copro.id)}
                            className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                              currentCoproId === copro.id ? 'bg-blue-100' : ''
                            }`}
                          >
                            <div className="font-medium">{copro.numero}</div>
                            <div className="text-sm text-gray-600">{copro.residence}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500 text-center">
                          Aucune copropriété trouvée
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Checkboxes Syndic/Gestion for Lefeuvre */}
            {formData.property_origin === 'lefeuvre' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type de bien Lefeuvre
                </label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.lefeuvre_syndic || false}
                      onChange={(e) => !readOnly && onChange({ 
                        ...formData, 
                        lefeuvre_syndic: e.target.checked 
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                      disabled={readOnly}
                    />
                    <span className="text-sm font-medium text-gray-700">Syndic</span>
                  </label>
                  
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.lefeuvre_gestion || false}
                      onChange={(e) => !readOnly && onChange({ 
                        ...formData, 
                        lefeuvre_gestion: e.target.checked 
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                      disabled={readOnly}
                    />
                    <span className="text-sm font-medium text-gray-700">Gestion</span>
                  </label>
                </div>
              </div>
            )}

            {/* Checkboxes Syndic/Gestion for NewCo */}
            {formData.property_origin === 'newco' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type de bien NewCo
                </label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.newco_syndic || false}
                      onChange={(e) => !readOnly && onChange({ 
                        ...formData, 
                        newco_syndic: e.target.checked 
                      })}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-5 w-5"
                      disabled={readOnly}
                    />
                    <span className="text-sm font-medium text-gray-700">Syndic</span>
                  </label>
                  
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.newco_gestion || false}
                      onChange={(e) => !readOnly && onChange({ 
                        ...formData, 
                        newco_gestion: e.target.checked 
                      })}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-5 w-5"
                      disabled={readOnly}
                    />
                    <span className="text-sm font-medium text-gray-700">Gestion</span>
                  </label>
                </div>
              </div>
            )}

            {/* Checkboxes Syndic/Gestion for Kermarrec Syndic */}
            {formData.property_origin === 'syndic' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type de bien Kermarrec Syndic
                </label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.syndic_syndic || false}
                      onChange={(e) => !readOnly && onChange({ 
                        ...formData, 
                        syndic_syndic: e.target.checked 
                      })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-5 w-5"
                      disabled={readOnly}
                    />
                    <span className="text-sm font-medium text-gray-700">Syndic</span>
                  </label>
                  
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.syndic_gestion || false}
                      onChange={(e) => !readOnly && onChange({ 
                        ...formData, 
                        syndic_gestion: e.target.checked 
                      })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-5 w-5"
                      disabled={readOnly}
                    />
                    <span className="text-sm font-medium text-gray-700">Gestion</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}